import { useCallback, useEffect, useRef, type MouseEvent as ReactMouseEvent } from "react";
import {
  TABLE_BORDER_PX,
  TABLE_CELL_MAX_LENGTH,
  cellInlineStylesOf,
  resolveTableObjectColumnWidths,
  resolveTableObjectRowHeights,
  type TableCanvasObject,
  type TableCellInlineStyles,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import { tableObjectCellDisplayText, tableObjectCellSpans } from "../../../lib/markdown-text";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { useObjectDrag } from "../input/use-object-drag";
import { useTableCellEditor } from "../input/use-table-cell-editor";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { resolveObjectCursor, useCanvasStore } from "../store/canvas-store";
import { renderInlineDecoratedSpans } from "./linkified-text";

interface Props {
  readonly object: TableCanvasObject;
}

/** 編集中セルの入力欄と、そのハンドラをセルへ配るための束。 */
interface TableGridEditing {
  readonly row: number;
  readonly column: number;
  readonly draft: string;
  /** 補助入力へ切り替えている間は、表の中に入力欄を描かない（入力 DOM を 2 個有効にしない）。 */
  readonly inline: boolean;
  readonly inputRef: (element: HTMLInputElement | null) => void;
  readonly inputHandlers: ReturnType<typeof useTableCellEditor>["inputHandlers"];
}

/** 表図形。セルのダブルクリック・F2・選択パネルから、セル本文を編集できる。 */
export function TableObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((state) => state.tool);
  const isSelected = useCanvasStore((state) => state.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((state) => state.readOnly);
  const isTrashed = useCanvasDocStore((state) => state.meta.isTrashed ?? false);
  const editor = useTableCellEditor(object);
  const editingCell = editor.editingCell;

  // 表グリッドの root は、セル編集器（編集領域の内外判定）と実描画の測定の両方から要る。
  // 1 つの要素へ ref を 2 つ渡すことはできないため、ここで合成する。
  const editorWrapperRef = editor.wrapperRef;
  const setGridElement = useCallback(
    (element: HTMLDivElement | null): void => {
      gridRef.current = element;
      editorWrapperRef(element);
    },
    [editorWrapperRef]
  );

  // 表の実描画の大きさへ枠を追随させる。行高の見積もり（resolveTableObjectRowHeights）は
  // 文字幅の近似であり、フォントによっては 1 行ぶん足りないことがある。行の高さを見積もりの
  // 下限として扱い（styles.css の cell-clip は min-height）、実測した大きさへ枠を広げることで、
  // セルの文字がクリップされないようにする。幅も同時に追随させ、列幅の合計が枠を超えたときの
  // 編集中スクロールを不要にする。
  const fitFrameToContent = useCallback((): void => {
    // 参照専用（埋め込み）では枠を変えない。表示しているだけのクライアントが
    // 他の参加者の表の大きさを書き換えてしまうのを防ぐ。
    if (readOnly) return;
    // ゴミ箱のキャンバスでも枠を変えない。この追随は利用者の編集操作ではないため、
    // 追随 API のゴミ箱ガード（編集を促す通知を出す）まで届かせてはいけない
    // （表を含むゴミ箱のキャンバスを開いただけで通知が出てしまう）。
    if (isTrashed) return;
    const element = gridRef.current;
    if (!element) return;
    // 未レイアウト（jsdom・非表示）では 0 になる。その場合は枠を変更しない。
    if (element.scrollHeight <= 0 || element.scrollWidth <= 0) return;
    // scrollHeight / scrollWidth は枠の内側（border を除く）の実寸のため、border 2 本ぶんを
    // 足して object.w / object.h と同じ基準にそろえる。
    dispatcher.autoFitObjectFrame(object.id, {
      h: element.scrollHeight + TABLE_BORDER_PX * 2,
      w: element.scrollWidth + TABLE_BORDER_PX * 2,
    });
  }, [dispatcher, isTrashed, object.id, readOnly]);

  // 内容（セル本文・文字サイズ・列構成）が変わったときと、セル編集の開始・終了で測り直す。
  // 枠の幅（object.w）も契機に含める。列幅が変わると折り返し行数が変わるため、幅を追随させた
  // 直後や利用者が幅を変えた直後に測り直さないと、必要な高さが足りないまま残る。
  // 枠の高さ（object.h）も契機に含める。利用者が枠を内容より低く縮めると、行の下限を超えた
  // 内容が外枠（overflow: hidden）で切り取られるため、実測した必要量へ戻す必要がある。
  // 追随で h が動いた直後の再測定は同じ実測値を渡すだけで、追随 API の 1px 未満の不感帯で
  // op にならず、往復にはならない。
  const isEditingCell = editingCell !== null;
  useEffect(() => {
    fitFrameToContent();
  }, [
    fitFrameToContent,
    isEditingCell,
    object.cells,
    object.cellInlineStyles,
    object.columnFractions,
    object.fontSize,
    object.h,
    object.w,
  ]);

  useObjectDrag({
    objectId: object.id,
    elementRef: ref,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    // 編集中は図形ドラッグへ流さない（セル内のマウス選択を奪わないため）。
    disabled: activeTool === "draw-pen" || editingCell !== null,
  });

  // ダブルクリックは、セルではなくこの root で受ける。
  // 図形ドラッグが pointerdown で setPointerCapture するため、続く click / dblclick は
  // ブラウザによって capture 要素（この root）へ付け替えられ、セルに置いたハンドラは呼ばれない。
  // 対象セルは event.target から引き、付け替えで root になっていた場合は座標から引き直す。
  const beginEditAt = editor.beginEditAt;
  const handleDoubleClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>): void => {
      const fromTarget =
        event.target instanceof Element ? event.target.closest("[data-table-cell]") : null;
      const cellElement =
        fromTarget ??
        document.elementFromPoint(event.clientX, event.clientY)?.closest("[data-table-cell]") ??
        null;
      const position = cellElement?.getAttribute("data-table-cell")?.split("-");
      if (!position || position.length !== 2) return;
      const row = Number(position[0]);
      const column = Number(position[1]);
      if (!Number.isInteger(row) || !Number.isInteger(column)) return;
      event.stopPropagation();
      beginEditAt(row, column);
    },
    [beginEditAt]
  );

  return (
    <div
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="table"
      onDoubleClick={handleDoubleClick}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        // 参照専用では動かせないため default。配置ツール・ペン描画の選択中は新規作成（crosshair）を示す。
        cursor: resolveObjectCursor({ readOnly, activeTool }),
        outline: isSelected
          ? "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)"
          : "none",
        // 編集中だけ文字選択を許可する（非編集時は図形として扱うため従来どおり抑止する）。
        userSelect: editingCell === null ? "none" : "text",
      }}
    >
      <TableGrid
        cells={object.cells}
        cellInlineStyles={object.cellInlineStyles}
        columnFractions={object.columnFractions}
        width={object.w}
        height={object.h}
        fontSize={object.fontSize ?? 16}
        color={object.color}
        wrapperRef={setGridElement}
        editing={
          editingCell === null
            ? undefined
            : {
                row: editingCell.row,
                column: editingCell.column,
                draft: editor.draft,
                inline: !editor.auxiliary,
                inputRef: editor.inputRef,
                inputHandlers: editor.inputHandlers,
              }
        }
      />
    </div>
  );
}

/**
 * セル本文（生テキスト）を、記法のインライン装飾を解釈した表示 span 列として描く。
 *
 * span への分解はテキスト図形の本文・Markdown 表のセルと同じ共有関数（`tableObjectCellSpans`）で、
 * 描画もテキスト図形と同じ共有部品（`renderInlineDecoratedSpans`）を使う。別の描画を書き起こすと、
 * 同じ記法が 2 通りの見た目になり得るためである。
 *
 * リンク記法（`[ラベル](URL)`）はラベルへ縮約した表示だけを行い、アンカーにはしない。
 * 表図形はダブルクリックがセル編集の開始であり、クリック系の操作をリンク遷移へ割り当てると
 * 編集導線と衝突するためである（リンクを開く導線が必要になった場合は別の設計で扱う）。
 *
 * セルの部分書式（`runs`）は、記法由来の装飾と同じ共有関数で重ねる。指定しない項目は
 * 表全体の文字色・文字サイズ（`TableGrid` の style）が基底としてそのまま効く。
 */
function TableCellText({
  cell,
  runs,
}: {
  readonly cell: string;
  readonly runs?: readonly TextInlineStyleRange[] | undefined;
}): JSX.Element {
  return <>{renderInlineDecoratedSpans(tableObjectCellSpans(cell, runs))}</>;
}

/** 確定済み表と配置プレビューで共有する 1 枚の表。 */
export function TableGrid({
  cells,
  cellInlineStyles,
  columnFractions,
  width,
  height,
  fontSize,
  color,
  testId,
  wrapperRef,
  editing,
}: {
  readonly cells: readonly (readonly string[])[];
  /** セルごとの部分書式。配置プレビューは渡さない（プレビューは本文だけを描く）。 */
  readonly cellInlineStyles?: TableCellInlineStyles | undefined;
  readonly columnFractions?: readonly number[] | undefined;
  readonly width: number;
  readonly height: number;
  readonly fontSize: number;
  readonly color?: string | undefined;
  readonly testId?: string | undefined;
  /**
   * 表グリッドの root を呼び出し側へ渡す ref。編集領域の内外判定（セル編集器）と、
   * 枠を追随させるための実描画の測定に使う。
   * 配置プレビューは渡さない（プレビューはフォーカス可能要素・イベント・op を一切持たない）。
   */
  readonly wrapperRef?: ((element: HTMLDivElement | null) => void) | undefined;
  readonly editing?: TableGridEditing | undefined;
}): JSX.Element {
  const columnWidths = resolveTableObjectColumnWidths({
    cells,
    columnFractions,
    availablePx: Math.max(0, width - TABLE_BORDER_PX * 2),
    fontPx: fontSize,
  });
  // 行高は列幅（折り返し行数の見積もりに要る）を確定してから解決する。
  // 列幅を行高の中で計算し直すと、片方だけ直したときに食い違う。
  //
  // 画面では、枠の余りの配分（availablePx）を渡さず、配分前の必要量だけを行の下限にする。
  // 枠を満たす配分はブラウザ（table の height）へ任せる。枠の高さを行の下限へ混ぜると、
  // 見積もりより多く折り返した行がある表で「枠が広がる → 余りが再配分されて下限が増える →
  // また枠が足りない」の往復になり、はみ出しが残る。
  const rowHeights = resolveTableObjectRowHeights({
    // 折り返し行数は、画面に実際に出る表示テキストで測る。生テキストのまま測ると、
    // `[ラベル](URL)` のように表示で縮約される記法を持つ行が無駄に高くなる。
    cellTexts: cells.map((row) => row.map(tableObjectCellDisplayText)),
    columnWidthsPx: columnWidths,
    // 部分書式で大きくした文字がある行は、その大きさで下限を出す（画面・PDF・サムネイルで同じ入力）。
    cellInlineStyles,
    availablePx: 0,
    fontPx: fontSize,
  });
  // 列の実寸合計を table 自身の幅に使い、最小幅超過時は外側で切り取る。
  const tableWidth = columnWidths.reduce((sum, value) => sum + value, 0);
  // 枠の内側の高さ。table の height は下限として働くため、内容が少ない表では各行へ余りが
  // 配られて枠を満たし、内容が多い表では内容の高さが優先される（枠は実測の追随で広がる）。
  const tableHeight = Math.max(0, height - TABLE_BORDER_PX * 2);
  const renderRow = (row: readonly string[], rowIndex: number, header: boolean): JSX.Element => {
    const Cell = header ? "th" : "td";
    return (
      <tr key={rowIndex}>
        {row.map((cell, columnIndex) => {
          const isEditing = editing?.row === rowIndex && editing.column === columnIndex;
          return (
            <Cell
              key={columnIndex}
              // ダブルクリックの対象特定・リモートキャレットの位置解決・E2E の取得に使う。
              // ダブルクリックのハンドラ自体はオブジェクト root 側に置く（pointer capture の
              // 付け替えでセルのハンドラが呼ばれないため）。
              data-table-cell={`${rowIndex}-${columnIndex}`}
              className={isEditing ? "canvas-table-object__cell--editing" : undefined}
            >
              <div
                className="canvas-table-object__cell-clip"
                // 見積もった行高は下限として渡す。実際の折り返しが見積もりより多いセルでは
                // ブラウザが箱を広げ、文字が切り取られない（枠は実測の追随で広がる）。
                style={{ minHeight: `${rowHeights[rowIndex] ?? 0}px` }}
              >
                {isEditing && editing.inline ? (
                  <input
                    ref={editing.inputRef}
                    type="text"
                    className="canvas-table-object__cell-input"
                    defaultValue={editing.draft}
                    maxLength={TABLE_CELL_MAX_LENGTH}
                    aria-label={`表のセル ${rowIndex + 1} 行 ${columnIndex + 1} 列`}
                    data-testid="table-cell-input"
                    {...editing.inputHandlers}
                  />
                ) : (
                  <span className="canvas-table-object__cell-text">
                    {/* 編集中は記法を書き換えられるよう生テキストを出し、確定後の表示だけ装飾を解釈する。 */}
                    {isEditing ? (
                      editing.draft
                    ) : (
                      <TableCellText
                        cell={cell}
                        runs={cellInlineStylesOf(cellInlineStyles, rowIndex, columnIndex)}
                      />
                    )}
                  </span>
                )}
              </div>
            </Cell>
          );
        })}
      </tr>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className="canvas-table-object"
      data-table-object="true"
      data-testid={testId}
      style={{ fontSize: `${fontSize}px`, color: color ?? "var(--color-text)" }}
    >
      <table
        className="canvas-md-table"
        style={{ width: `${tableWidth}px`, height: `${tableHeight}px` }}
      >
        <colgroup>
          {columnWidths.map((columnWidth, index) => (
            <col key={index} style={{ width: `${columnWidth}px` }} />
          ))}
        </colgroup>
        <thead>{cells[0] ? renderRow(cells[0], 0, true) : null}</thead>
        <tbody>{cells.slice(1).map((row, index) => renderRow(row, index + 1, false))}</tbody>
      </table>
    </div>
  );
}
